import type { ResumeConfirmResponse } from '@rheora/enrollment-contract';

/**
 * E-CRM04 — the `/resume/confirm` page's decisions. PURE: no React, no network.
 */

/** Ten NANP digits, formatting ignored, or null. The BFF compares the last ten. */
export function normalizeConfirmPhone(raw: string): string | null {
  const digits = raw.replace(/\D/gu, '');
  if (digits.length === 10) return digits;
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  return null;
}

/** The `location.state` flag the landing page reads to show the neutral notice. */
export const RESUME_RESTART_STATE = { resumeNotice: 'restart' } as const;

/** True when a navigation carried `RESUME_RESTART_STATE`. */
export function isResumeRestartState(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    'resumeNotice' in state &&
    state.resumeNotice === 'restart'
  );
}

export type ResumeConfirmAction =
  /** Restored: FULL navigation, so the hydration gate reads the seeded session. */
  | { readonly kind: 'restored'; readonly to: string }
  /** Wrong number, tries left. */
  | { readonly kind: 'retry'; readonly attemptsLeft: number }
  /** Locked out / expired / skipped: start over at `/` with a neutral notice. */
  | { readonly kind: 'restart' };

/** "…1 try left" vs "…2 tries left" — the i18n key for the mismatch line. */
export function mismatchKey(attemptsLeft: number): string {
  return attemptsLeft === 1 ? 'shop.resume.confirm.mismatchOne' : 'shop.resume.confirm.mismatchOther';
}

/** Turn the BFF's answer into what the page does next. */
export function decideResumeConfirm(res: ResumeConfirmResponse): ResumeConfirmAction {
  if (res.ok && res.redirectTo !== undefined) return { kind: 'restored', to: res.redirectTo };
  const left = res.attemptsLeft ?? 0;
  if (!res.ok && left > 0) return { kind: 'retry', attemptsLeft: left };
  return { kind: 'restart' };
}
